import { useState } from "react";
import FileUpload from "@/components/FileUpload";
import MaterialSelect, { Material } from "@/components/MaterialSelect";
import PriceBreakdown from "@/components/PriceBreakdown";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Mail } from "lucide-react";
import { toast } from "sonner";

const MATERIALS: Material[] = [
  { 
    id: 'pla-nx2',
    brand: 'extrudr',
    name: 'PLA NX2 MATT',
    color: '',
    pricePerKg: 26.00
  },
  { 
    id: 'petg',
    brand: 'extrudr',
    name: 'PETG',
    color: '',
    pricePerKg: 35.45
  },
  { 
    id: 'flex-medium',
    brand: 'extrudr',
    name: 'FLEX Medium',
    color: '',
    pricePerKg: 46.67
  },
  { 
    id: 'durapro-abs',
    brand: 'extrudr',
    name: 'DuraPro ABS',
    color: '',
    pricePerKg: 33.33
  }
];

const PRE_PROCESSING_BASE = 15;
const PRE_PROCESSING_PER_MODEL = 3.5;
const POST_PROCESSING_BASE = 15;
const POST_PROCESSING_PER_MODEL = 2.5;

interface ModelData {
  file: File;
  volume: number;
  quantity: number;
}

const Calculator = () => {
  const [models, setModels] = useState<ModelData[]>([]);
  const [selectedMaterial, setSelectedMaterial] = useState<Material>(MATERIALS[0]);

  const calculateSTLVolume = async (file: File): Promise<number> => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const buffer = e.target?.result as ArrayBuffer;
        const view = new DataView(buffer);
        let offset = 80; // Skip header
        const triangles = view.getUint32(offset, true);
        let volume = 0;
        
        offset += 4;
        for (let i = 0; i < triangles; i++) {
          const nx = view.getFloat32(offset + 0, true);
          const ny = view.getFloat32(offset + 4, true);
          const nz = view.getFloat32(offset + 8, true);
          
          const x1 = view.getFloat32(offset + 12, true);
          const y1 = view.getFloat32(offset + 16, true);
          const z1 = view.getFloat32(offset + 20, true);
          
          const x2 = view.getFloat32(offset + 24, true);
          const y2 = view.getFloat32(offset + 28, true);
          const z2 = view.getFloat32(offset + 32, true);
          
          const x3 = view.getFloat32(offset + 36, true);
          const y3 = view.getFloat32(offset + 40, true);
          const z3 = view.getFloat32(offset + 44, true);
          
          const v321 = x3 * y2 * z1;
          const v231 = x2 * y3 * z1;
          const v312 = x3 * y1 * z2;
          const v132 = x1 * y3 * z2;
          const v213 = x2 * y1 * z3;
          const v123 = x1 * y2 * z3;
          
          const signedVolume = (-v321 + v231 + v312 - v132 - v213 + v123) / 6.0;
          volume += signedVolume;
          
          offset += 50; // Move to next triangle
        }
        resolve(Math.abs(volume) / 1000); // Convert to cm³
      };
      reader.readAsArrayBuffer(file);
    });
  };

  const handleFilesSelected = async (files: File[]) => {
    const newModels = await Promise.all(
      files.map(async (file) => ({
        file,
        volume: await calculateSTLVolume(file),
        quantity: 1
      }))
    );
    setModels([...models, ...newModels]);
  };

  const handleFileRemove = (fileName: string) => {
    setModels(models.filter(model => model.file.name !== fileName));
  };

  const handleMaterialChange = (materialId: string) => {
    const material = MATERIALS.find((m) => m.id === materialId);
    if (material) {
      setSelectedMaterial(material);
    }
  };

  const handleQuantityChange = (index: number, quantity: number) => {
    setModels(prevModels => 
      prevModels.map((model, i) => 
        i === index ? { ...model, quantity: quantity } : model
      )
    );
  };

  const totalVolume = models.reduce((sum, model) => sum + (model.volume * model.quantity), 0);
  const totalQuantity = models.reduce((sum, model) => sum + model.quantity, 0);
  
  const MATERIAL_DENSITY = 0.6; // g/cm³
  const volumeInCm3 = totalVolume;
  const weightInGrams = volumeInCm3 * MATERIAL_DENSITY;
  const materialWeight = weightInGrams / 1000; // Convert to kg
  const materialCost = materialWeight * selectedMaterial.pricePerKg;
  
  const energyCost = 2.5;
  const preProcessingCost = PRE_PROCESSING_BASE + (PRE_PROCESSING_PER_MODEL * (totalQuantity - 1));
  const postProcessingCost = POST_PROCESSING_BASE + (POST_PROCESSING_PER_MODEL * (totalQuantity - 1));
  const wearAndTearCost = 10;

  const totalCost = materialCost + energyCost + preProcessingCost + postProcessingCost + wearAndTearCost;

  const costDetails = {
    materialCostPerKg: selectedMaterial.pricePerKg,
    energyCostPerHour: 0,
    preProcessingBaseRate: PRE_PROCESSING_BASE,
    preProcessingPerModel: PRE_PROCESSING_PER_MODEL,
    postProcessingBaseRate: POST_PROCESSING_BASE,
    postProcessingPerModel: POST_PROCESSING_PER_MODEL,
    wearAndTearPerHour: 0,
  };

  const handleSendData = async () => {
    try {
      const formData = new FormData();
      
      // Add model files
      models.forEach((model, index) => {
        formData.append(`model-${index}`, model.file);
      });

      // Add calculation details
      const calculationDetails = {
        material: selectedMaterial,
        totalVolume: volumeInCm3,
        materialWeight: materialWeight,
        costs: {
          material: materialCost,
          energy: energyCost,
          preProcessing: preProcessingCost,
          postProcessing: postProcessingCost,
          wearAndTear: wearAndTearCost,
          total: totalCost
        },
        models: models.map(m => ({
          name: m.file.name,
          volume: m.volume,
          quantity: m.quantity
        }))
      };

      formData.append('calculationDetails', JSON.stringify(calculationDetails));

      // Create mailto link with calculation details
      const mailtoLink = `mailto:konstantin.florian.kalla@3d-druck-potsdam.de?subject=3D Druck Anfrage&body=${encodeURIComponent(
        `Calculation Details:\n\n` +
        `Material: ${selectedMaterial.name}\n` +
        `Total Volume: ${volumeInCm3.toFixed(2)} cm³\n` +
        `Material Weight: ${materialWeight.toFixed(2)} kg\n\n` +
        `Costs:\n` +
        `Material: ${materialCost.toFixed(2)} €\n` +
        `Energy: ${energyCost.toFixed(2)} €\n` +
        `Pre-processing: ${preProcessingCost.toFixed(2)} €\n` +
        `Post-processing: ${postProcessingCost.toFixed(2)} €\n` +
        `Wear and Tear: ${wearAndTearCost.toFixed(2)} €\n` +
        `Total: ${totalCost.toFixed(2)} €\n\n` +
        `Models:\n${models.map(m => `${m.file.name} - Volume: ${m.volume.toFixed(2)} cm³ - Quantity: ${m.quantity}`).join('\n')}`
      )}`;

      // Open default email client
      window.location.href = mailtoLink;
      toast.success('Email client opened with calculation details');
    } catch (error) {
      toast.error('Failed to prepare email data');
      console.error('Error preparing email data:', error);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-24">
        <h2 className="text-2xl font-medium text-[#000000] mb-4">3D Kostenrechner</h2>
        <p className="text-[#666666] mb-8">
          Berechnen Sie die Kosten für Ihren individuellen 3D-Druck
        </p>
        
        <div className="grid gap-8 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Modelle hochladen</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <FileUpload 
                onFilesSelected={handleFilesSelected}
                onFileRemove={handleFileRemove}
                files={models.map(m => m.file)}
              />
              {models.map((model, index) => (
                <div key={index}>
                  <Label>Datei {index + 1}: {model.file.name}</Label>
                  <div className="mt-2">
                    <Label htmlFor={`quantity-${index}`}>Anzahl</Label>
                    <Input
                      id={`quantity-${index}`}
                      type="number"
                      min="1"
                      value={model.quantity}
                      onChange={(e) => handleQuantityChange(index, parseInt(e.target.value) || 1)}
                      className="mt-1"
                    />
                  </div>
                </div>
              ))}
              <div>
                <Label>Material auswählen</Label>
                <MaterialSelect
                  materials={MATERIALS}
                  selectedMaterial={selectedMaterial}
                  onMaterialChange={handleMaterialChange}
                />
              </div>
            </CardContent>
          </Card>

          <div className="space-y-4">
            <PriceBreakdown
              materialCost={materialCost}
              energyCost={energyCost}
              preProcessingCost={preProcessingCost}
              postProcessingCost={postProcessingCost}
              wearAndTearCost={wearAndTearCost}
              totalCost={totalCost}
              models={models.map(m => ({
                name: m.file.name,
                volumeCm3: m.volume,
                quantity: m.quantity
              }))}
              costDetails={costDetails}
            />
            
            <Button 
              onClick={handleSendData}
              className="w-full"
            >
              <Mail className="mr-2" />
              Anfrage senden
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Calculator;
